import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck} from '../../helpers'
import {
  StaffTenantLookupClient,
  AccountTenantLookupClient,
  ExtendedAccountLookupClient,
  BasicLookupClient,
  DistrictLookupClient,
  AllProductTenantLookupClient,
  StockProductTenantLookupClient,
  WarehouseTenantLookupClient,
  ProjectTenantLookupClient,
  AllProjectTenantLookupClient,
  AllCompanyTenantLookupClient,
  GetUsableCheckTenantLookupClient,
  AllRoleTenantLookupClient,
  AllProductForUpdateClient,
  ScoreCardLookupClient,
  DynamicReportRoleLookupClient,
  ConstructionSiteTenantLookupClient,
  BlockTenantLookupClient,
  PlotTenantLookupClient,
  BuildingBlockTypeTenantLookupClient,
  AdvanceCheckLookupClient,
  AllAccountLookupClient,
  AllCompanyByProjectLookupClient,
  CompanyInformationLookupClient,
  UserByUserProjectTenantLookupClient,
  AllUserTenantLookupClient,
  ProposalCompanyLookupClient,
  ProjectUsersTenantLookupClient
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const basicLookupClient = new BasicLookupClient(SERVICE_URL)
const staffTenantLookupClient = new StaffTenantLookupClient(SERVICE_URL)
const accountTenantLookupClient = new AccountTenantLookupClient(SERVICE_URL)
const extendedAccountLookupClient = new ExtendedAccountLookupClient(SERVICE_URL)
const districtLookupClient = new DistrictLookupClient(SERVICE_URL)
const allProductTenantLookupClient = new AllProductTenantLookupClient(SERVICE_URL)
const stockProductTenantLookupClient = new StockProductTenantLookupClient(SERVICE_URL)
const warehouseTenantLookupClient = new WarehouseTenantLookupClient(SERVICE_URL)
const projectTenantLookupClient = new ProjectTenantLookupClient(SERVICE_URL)
const allProjectTenantLookupClient = new AllProjectTenantLookupClient(SERVICE_URL)
const allCompanyTenantLookupClient = new AllCompanyTenantLookupClient(SERVICE_URL)
const getUsableCheckTenantLookupClient = new GetUsableCheckTenantLookupClient(SERVICE_URL)
const allRoleTenantLookupClient = new AllRoleTenantLookupClient(SERVICE_URL)
const allProductForUpdateClient = new AllProductForUpdateClient(SERVICE_URL)
const scoreCardLookupClient = new ScoreCardLookupClient(SERVICE_URL)
const dynamicReportRoleLookupClient = new DynamicReportRoleLookupClient(SERVICE_URL)
const constructionSiteTenantLookupClient = new ConstructionSiteTenantLookupClient(SERVICE_URL)
const blockTenantLookupClient = new BlockTenantLookupClient(SERVICE_URL)
const plotTenantLookupClient = new PlotTenantLookupClient(SERVICE_URL)
const buildingBlockTypeTenantLookupClient = new BuildingBlockTypeTenantLookupClient(SERVICE_URL)
const advanceCheckLookupClient = new AdvanceCheckLookupClient(SERVICE_URL)
const allAccountLookupClient = new AllAccountLookupClient(SERVICE_URL)
const allCompanyByProjectLookupClient = new AllCompanyByProjectLookupClient(SERVICE_URL)
const companyInformationLookupClient = new CompanyInformationLookupClient(SERVICE_URL)
const userByUserProjectTenantLookupClient = new UserByUserProjectTenantLookupClient(SERVICE_URL)
const allUserTenantLookupClient = new AllUserTenantLookupClient(SERVICE_URL)
const proposalCompanyLookupClient = new ProposalCompanyLookupClient(SERVICE_URL)
const projectUsersTenantLookupClient = new ProjectUsersTenantLookupClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  staffLookup: null,
  accountLookupList: null,
  extendedAccountLookup: null,
  districtLookup: null,
  productLookup: null,
  stockProductLookup: null,
  warehouseLookupList: null,
  projectLookupList: null,
  allProjectLookupList: null,
  companyLookup: null,
  usableCheckLookup: null,
  roleLookup: null,
  allProductForUpdateList: null,
  scoreCardLookup: null,
  dynamicReportRoleLookupList: null,
  constructionSiteLookup: null,
  blockLookup: null,
  plotLookup: null,
  buildingBlockTypeLookupList: null,
  advanceCheckLookup: null,
  allAccountLookup: null,
  allCompanyByProjectLookup: null,
  companyInformationLookup: null,
  userProjectLookup: null,
  allUserLookupList: null,
  proposalCompanyLookupList: null,
  projectUsersLookupList: null,
}

// Async Thunks
export const basicLookup = createAsyncThunk('lookup/basic', async (name, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await basicLookupClient.basicLookup({name})
    dispatch(setBasicLookup({name, data: response}))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const staffGetAll = createAsyncThunk('lookup/staff', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await staffTenantLookupClient.staffLookup({})
    dispatch(setStaffLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const bankCashAccountGetAll = createAsyncThunk('lookup/account', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await accountTenantLookupClient.accountLookup({})
    dispatch(setAccountLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const extendedBankCashAccountGetAll = createAsyncThunk(
  'lookup/extendedAccount',
  async (_, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await extendedAccountLookupClient.extendedAccountLookup({})
      dispatch(setExtendedAccountLookup(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const districtLookup = createAsyncThunk('lookup/district', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await districtLookupClient.districtLookup(params)
    dispatch(setDistrictLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const allProductLookup = createAsyncThunk('lookup/product', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await allProductTenantLookupClient.productLookup({})
    dispatch(setProductLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const stockProductLookup = createAsyncThunk('lookup/stockProduct', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await stockProductTenantLookupClient.stockProductLookup({})
    dispatch(setStockProductLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const warehouseLookup = createAsyncThunk('lookup/warehouse', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await warehouseTenantLookupClient.warehouseLookup({})
    dispatch(setWarehouseLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const projectLookup = createAsyncThunk('lookup/project', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await projectTenantLookupClient.projectLookup({})
    dispatch(setProjectLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const allProjectLookup = createAsyncThunk('lookup/allProject', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await allProjectTenantLookupClient.allProjectLookup({})
    dispatch(setAllProjectLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const allCompanyLookup = createAsyncThunk('lookup/company', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await allCompanyTenantLookupClient.companyLookup({})
    dispatch(setCompanyLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const usableCheckLookup = createAsyncThunk('lookup/usableCheck', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await getUsableCheckTenantLookupClient.getUsableCheckLookup({})
    dispatch(setUsableCheckLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const allRoleLookup = createAsyncThunk('lookup/role', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await allRoleTenantLookupClient.roleLookup({})
    dispatch(setRoleLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const allProductForUpdate = createAsyncThunk(
  'lookup/productForUpdate',
  async (_, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await allProductForUpdateClient.allProductForUpdate({})
      dispatch(setAllProductForUpdate(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const scoreCardLookup = createAsyncThunk('lookup/scoreCard', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await scoreCardLookupClient.scoreCardLookup({})
    dispatch(setScoreCardLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const dynamicReportRoleLookup = createAsyncThunk(
  'lookup/dynamicReportRole',
  async (_, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await dynamicReportRoleLookupClient.dynamicReportRoleLookup({})
      dispatch(setDynamicReportRoleLookupList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const constructionSiteLookup = createAsyncThunk(
  'lookup/constructionSite',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await constructionSiteTenantLookupClient.constructionSiteLookup(params)
      dispatch(setConstructionSiteLookup(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const blockLookup = createAsyncThunk('lookup/block', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await blockTenantLookupClient.blockLookup(params)
    dispatch(setBlockLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const plotLookup = createAsyncThunk('lookup/plot', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await plotTenantLookupClient.plotLookup(params)
    dispatch(setPlotLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const buildingBlockTypeLookup = createAsyncThunk(
  'lookup/buildingBlockType',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await buildingBlockTypeTenantLookupClient.buildingBlockTypeLookup(params)
      dispatch(setBuildingBlockTypeLookup(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const advanceCheckLookup = createAsyncThunk(
  'lookup/advanceCheck',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await advanceCheckLookupClient.getAdvanceCheckLookup(params)
      dispatch(setAdvanceCheckLookup(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const allAccountLookup = createAsyncThunk('lookup/allAccount', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await allAccountLookupClient.allAccountLookup()
    dispatch(setAllAccountLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const allCompanyByProjectLookup = createAsyncThunk(
  'lookup/allCompanyByProject',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await allCompanyByProjectLookupClient.getAllCompanyByProjectLookup(params)
      dispatch(setAllCompanyByProjectLookup(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const companyInformationLookup = createAsyncThunk(
  'lookup/companyInformation',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await companyInformationLookupClient.getCompanyInformationLookup(params)
      dispatch(setCompanyInformationLookup(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const userProjectLookup = createAsyncThunk('lookup/userProject', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await userByUserProjectTenantLookupClient.usersLookup()
    dispatch(setUserProjectLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const allUserLookup = createAsyncThunk('lookup/allUser', async (_, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await allUserTenantLookupClient.userLookup()
    dispatch(setAllUserLookupList(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const proposalCompanyLookup = createAsyncThunk(
  'lookup/proposalCompany',
  async (_, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await proposalCompanyLookupClient.proposalCompanyLookup({})
      dispatch(setProposalCompanyLookupList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const projectUsersLookup = createAsyncThunk('lookup/projectUsers', async (projectId, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await projectUsersTenantLookupClient.projectUsersLookup(projectId)
    dispatch(setProjectUsersLookup(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

// Slice
const lookupSlice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setBasicLookup: (state, action) => {
      state[action.payload.name] = action.payload.data
      state.loading = false
    },
    setStaffLookup: (state, action) => {
      state.staffLookup = action.payload
      state.loading = false
    },
    setAccountLookup: (state, action) => {
      state.accountLookupList = action.payload
      state.loading = false
    },
    setExtendedAccountLookup: (state, action) => {
      state.extendedAccountLookup = action.payload
      state.loading = false
    },
    setDistrictLookup: (state, action) => {
      state.District = action.payload
      state.loading = false
    },
    setProductLookup: (state, action) => {
      state.productLookup = action.payload
      state.loading = false
    },
    setStockProductLookup: (state, action) => {
      state.stockProductLookup = action.payload
      state.loading = false
    },
    setWarehouseLookup: (state, action) => {
      state.warehouseLookupList = action.payload
      state.loading = false
    },
    setProjectLookup: (state, action) => {
      state.projectLookupList = action.payload
      state.loading = false
    },
    setAllProjectLookup: (state, action) => {
      state.allProjectLookupList = action.payload
      state.loading = false
    },
    setCompanyLookup: (state, action) => {
      state.companyLookup = action.payload
      state.loading = false
    },
    setUsableCheckLookup: (state, action) => {
      state.usableCheckLookupList = action.payload
      state.loading = false
    },
    setRoleLookup: (state, action) => {
      state.roleLookup = action.payload
      state.loading = false
    },
    setAllProductForUpdate: (state, action) => {
      state.allProductForUpdateList = action.payload
      state.loading = false
    },
    setScoreCardLookup: (state, action) => {
      state.scoreCardLookup = action.payload
      state.loading = false
    },
    setDynamicReportRoleLookupList: (state, action) => {
      state.dynamicReportRoleLookupList = action.payload
      state.loading = false
    },
    setConstructionSiteLookup: (state, action) => {
      state.constructionSiteLookupList = action.payload
      state.loading = false
    },
    setBlockLookup: (state, action) => {
      state.blockLookupList = action.payload
      state.loading = false
    },
    setPlotLookup: (state, action) => {
      state.plotLookupList = action.payload
      state.loading = false
    },
    setBuildingBlockTypeLookup: (state, action) => {
      state.buildingBlockTypeLookupList = action.payload
      state.loading = false
    },
    setAdvanceCheckLookup: (state, action) => {
      state.advanceCheckLookupList = action.payload
      state.loading = false
    },
    setAllAccountLookup: (state, action) => {
      state.allAccountLookupList = action.payload
      state.loading = false
    },
    setAllCompanyByProjectLookup: (state, action) => {
      state.allCompanyByProjectLookupList = action.payload
      state.loading = false
    },
    setCompanyInformationLookup: (state, action) => {
      state.companyInformationLookupList = action.payload
      state.loading = false
    },
    setUserProjectLookup: (state, action) => {
      state.userProjectLookupList = action.payload
      state.loading = false
    },
    setAllUserLookupList: (state, action) => {
      state.allUserLookupList = action.payload
      state.loading = false
    },
    setProposalCompanyLookupList: (state, action) => {
      state.proposalCompanyLookupList = action.payload
      state.loading = false
    },
    setProjectUsersLookup: (state, action) => {
      state.projectUsersLookupList = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('lookup/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setBasicLookup,
  setStaffLookup,
  setAccountLookup,
  setExtendedAccountLookup,
  setDistrictLookup,
  setProductLookup,
  setStockProductLookup,
  setWarehouseLookup,
  setProjectLookup,
  setAllProjectLookup,
  setCompanyLookup,
  setUsableCheckLookup,
  setRoleLookup,
  setAllProductForUpdate,
  setScoreCardLookup,
  setDynamicReportRoleLookupList,
  setConstructionSiteLookup,
  setBlockLookup,
  setPlotLookup,
  setBuildingBlockTypeLookup,
  setAdvanceCheckLookup,
  setAllAccountLookup,
  setAllCompanyByProjectLookup,
  setCompanyInformationLookup,
  setUserProjectLookup,
  setAllUserLookupList,
  setProposalCompanyLookupList,
  setProjectUsersLookup,
  resetState,
} = lookupSlice.actions

// Selectors
export const selectLookupLoading = (state) => state.lookup.loading
export const selectLookupError = (state) => state.lookup.error
export const selectBasicLookup = (state) => state.lookup
export const selectStaffLookup = (state) => state.lookup.staffLookup
export const selectAccountLookupList = (state) => state.lookup.accountLookupList
export const selectExtendedAccountLookup = (state) => state.lookup.extendedAccountLookup
export const selectDistrictLookup = (state) => state.lookup.District
export const selectProductLookup = (state) => state.lookup.productLookup
export const selectStockProductLookup = (state) => state.lookup.stockProductLookup
export const selectWarehouseLookupList = (state) => state.lookup.warehouseLookupList
export const selectProjectLookupList = (state) => state.lookup.projectLookupList
export const selectAllProjectLookupList = (state) => state.lookup.allProjectLookupList
export const selectCompanyLookup = (state) => state.lookup.companyLookup
export const selectUsableCheckLookup = (state) => state.lookup.usableCheckLookup
export const selectRoleLookup = (state) => state.lookup.roleLookup
export const selectAllProductForUpdateList = (state) => state.lookup.allProductForUpdateList
export const selectScoreCardLookup = (state) => state.lookup.scoreCardLookup
export const selectDynamicReportRoleLookupList = (state) => state.lookup.dynamicReportRoleLookupList
export const selectConstructionSiteLookupList = (state) => state.lookup.constructionSiteLookupList
export const selectBlockLookup = (state) => state.lookup.blockLookup
export const selectPlotLookup = (state) => state.lookup.plotLookup
export const selectBuildingBlockTypeLookupList = (state) => state.lookup.buildingBlockTypeLookupList
export const selectAdvanceCheckLookupList = (state) => state.lookup.advanceCheckLookupList
export const selectAllAccountLookupList = (state) => state.lookup.allAccountLookupList
export const selectAllCompanyByProjectLookupList = (state) =>
  state.lookup.allCompanyByProjectLookupList
export const selectCompanyInformationLookupList = (state) =>
  state.lookup.companyInformationLookupList
export const selectUserProjectLookupList = (state) => state.lookup.userProjectLookupList
export const selectAllUserLookupList = (state) => state.lookup.allUserLookupList
export const selectProposalCompanyLookupList = (state) => state.lookup.proposalCompanyLookupList
export const selectProjectUsersLookupList = (state) => state.lookup.projectUsersLookupList

export default lookupSlice.reducer
